import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { StatusBadge } from "@/components/badges/StatusBadge";
import { PlatformBadge } from "@/components/badges/PlatformBadge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ApprovalActions } from "@/components/content/ApprovalActions";
import { ScheduleAssetDialog } from "@/components/content/ScheduleAssetDialog";
import { RevisionConsole } from "@/components/content/RevisionConsole";
import { PlatformPreviewPanel } from "@/components/content/PlatformPreviewPanel";
import { Copy, CalendarPlus, Replace, Save, CheckCheck, Image as ImageIcon, Upload, Trash2, Loader2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAppState } from "@/state/AppStateProvider";
import { ASSET_TYPE_LABELS } from "@/lib/constants";
import { getAssetCoverPrompt } from "@/lib/imagePromptAssets";
import { imageAspectForPlatform } from "@/lib/mediaAspect";

export function EditablePostCard({ asset, className }) {
  const { updateAsset, approveAsset, rejectAsset, generateAssetCover, uploadAssetCover } = useAppState();
  const [draft, setDraft] = useState({ hook: asset.hook, body: asset.body, cta: asset.cta, hashtags: (asset.hashtags || []).join(" ") });
  const [saving, setSaving] = useState(false);
  const [revisionMode, setRevisionMode] = useState(null);
  const [coverBusy, setCoverBusy] = useState(false);
  const [coverPrompt, setCoverPrompt] = useState(() => getAssetCoverPrompt(asset));
  const coverInputRef = useRef(null);

  useEffect(() => {
    setCoverPrompt(getAssetCoverPrompt(asset));
  }, [asset]);

  const onGenerateCover = async () => {
    const prompt = (coverPrompt || "").trim();
    if (!prompt) return toast.error("Add an image prompt first");
    setCoverBusy(true);
    const pending = toast.loading("Generating image…");
    try {
      const res = await generateAssetCover(asset.id, prompt, undefined, imageAspectForPlatform(asset.platform));
      res?.ok ? toast.success("Image generated", { id: pending }) : toast.error(`Failed: ${res?.message || "unknown error"}`, { id: pending });
    } finally { setCoverBusy(false); }
  };

  const onUploadCover = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Pick an image file");
    if (file.size > 12 * 1024 * 1024) return toast.error("Image too large (max 12 MB)");
    const reader = new FileReader();
    reader.onload = async () => {
      setCoverBusy(true);
      const pending = toast.loading("Uploading image…");
      try {
        const res = await uploadAssetCover(asset.id, reader.result);
        res?.ok ? toast.success("Image uploaded", { id: pending }) : toast.error(`Failed: ${res?.message || "unknown error"}`, { id: pending });
      } finally { setCoverBusy(false); }
    };
    reader.readAsDataURL(file);
  };

  const onSave = () => {
    setSaving(true);
    setTimeout(() => {
      updateAsset(asset.id, {
        hook: draft.hook,
        body: draft.body,
        cta: draft.cta,
        hashtags: draft.hashtags.split(/\s+/).filter((t) => t.startsWith("#")),
      });
      setSaving(false);
      toast.success("Saved");
    }, 500);
  };

  const onCopy = () => {
    const text = `${draft.hook}\n\n${draft.body}\n\n${draft.cta}\n\n${draft.hashtags}`;
    navigator.clipboard?.writeText(text);
    toast.success("Copied to clipboard");
  };

  const liveAsset = {
    ...asset,
    hook: draft.hook,
    body: draft.body,
    cta: draft.cta,
    hashtags: draft.hashtags.split(/\s+/).filter((t) => t.startsWith("#")),
  };

  const onApplyRevision = (patch) => {
    updateAsset(asset.id, patch);
    setDraft((current) => ({
      ...current,
      hook: patch.hook ?? current.hook,
      body: patch.body ?? current.body,
      cta: patch.cta ?? current.cta,
    }));
    setRevisionMode(null);
  };

  return (
    <article
      data-testid="content-card"
      className={cn("overflow-hidden rounded-xl border bg-card inner-highlight", className)}
      style={{ borderColor: "var(--border-subtle)" }}
    >
      <header className="flex flex-wrap items-start justify-between gap-3 border-b px-4 py-3" style={{ borderColor: "var(--border-subtle)" }}>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <PlatformBadge platform={asset.platform} />
            <span className="rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-[0.14em] text-muted-foreground" style={{ borderColor: "var(--border-subtle)" }}>{ASSET_TYPE_LABELS[asset.asset_type]}</span>
            <span className="text-[11px] text-muted-foreground">Skill: <span className="text-foreground/80">{asset.skill_used}</span></span>
          </div>
          <h3 className="mt-1 text-[15px] font-semibold tracking-[-0.005em] text-1 line-clamp-2">{asset.title}</h3>
        </div>
        <StatusBadge status={asset.status} />
      </header>

      <Tabs defaultValue="body" className="px-4 pt-3">
        <TabsList className="bg-transparent p-0 gap-1">
          <TabsTrigger value="hook" className="h-7 px-2 text-xs">Hook</TabsTrigger>
          <TabsTrigger value="body" className="h-7 px-2 text-xs">Body</TabsTrigger>
          <TabsTrigger value="cta" className="h-7 px-2 text-xs">CTA</TabsTrigger>
          <TabsTrigger value="hashtags" className="h-7 px-2 text-xs">Hashtags</TabsTrigger>
          <TabsTrigger value="image" className="h-7 px-2 text-xs">Image</TabsTrigger>
        </TabsList>

        <TabsContent value="hook" className="mt-3">
          <Textarea
            data-testid="post-editor-hook"
            value={draft.hook}
            onChange={(e) => setDraft((d) => ({ ...d, hook: e.target.value }))}
            rows={2}
            className="resize-none"
            style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }}
          />
        </TabsContent>
        <TabsContent value="body" className="mt-3">
          <Textarea
            data-testid="post-editor-body"
            value={draft.body}
            onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
            rows={8}
            className="resize-y leading-[1.6]"
            style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }}
          />
        </TabsContent>
        <TabsContent value="cta" className="mt-3">
          <Textarea
            data-testid="post-editor-cta"
            value={draft.cta}
            onChange={(e) => setDraft((d) => ({ ...d, cta: e.target.value }))}
            rows={2}
            className="resize-none"
            style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }}
          />
        </TabsContent>
        <TabsContent value="hashtags" className="mt-3">
          <Input
            data-testid="post-editor-hashtags"
            value={draft.hashtags}
            onChange={(e) => setDraft((d) => ({ ...d, hashtags: e.target.value }))}
            placeholder="#AI #Agents"
            style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }}
          />
        </TabsContent>
        <TabsContent value="image" className="mt-3" data-testid="post-editor-image">
          {asset.cover_image && (
            <div className="mb-3 overflow-hidden rounded-lg border" style={{ borderColor: "var(--border-subtle)" }}>
              <img src={asset.cover_image} alt="Post cover" className="block max-h-[420px] w-full object-contain" />
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" className="gap-1.5" onClick={onGenerateCover} disabled={coverBusy} data-testid="post-editor-image-generate">
              {coverBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />}
              {asset.cover_image ? "Regenerate" : "Generate"}
            </Button>
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => coverInputRef.current?.click()} disabled={coverBusy} data-testid="post-editor-image-upload">
              <Upload className="h-3.5 w-3.5" /> Upload
            </Button>
            {asset.cover_image && (
              <Button size="sm" variant="ghost" className="gap-1.5 text-muted-foreground" onClick={() => updateAsset(asset.id, { cover_image: null })} disabled={coverBusy} data-testid="post-editor-image-remove">
                <Trash2 className="h-3.5 w-3.5" /> Remove
              </Button>
            )}
            <input ref={coverInputRef} type="file" accept="image/*" className="hidden" onChange={onUploadCover} data-testid="post-editor-image-input" />
          </div>
          <Textarea
            value={coverPrompt}
            onChange={(e) => setCoverPrompt(e.target.value)}
            rows={2}
            placeholder="Describe the image to generate…"
            className="mt-2 resize-none text-[12.5px]"
            style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }}
            data-testid="post-editor-image-prompt"
          />
          <p className="mt-1 text-[11px] text-muted-foreground">Changes this post's image only. Describe a scene, not text to render.</p>
        </TabsContent>
      </Tabs>

      <div className="px-4 pt-4">
        <PlatformPreviewPanel
          asset={liveAsset}
          compact
          title="Live edit preview"
          className="shadow-none"
          bodyClassName="bg-[color:var(--surface-2)]"
        />
      </div>

      <footer className="flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3 mt-4" style={{ borderColor: "var(--border-subtle)" }}>
        <div className="flex items-center gap-3 text-[11px] font-mono text-muted-foreground">
          <span data-testid="post-editor-charcount">{(draft.body || "").length}/{asset.char_limit}</span>
          <span className="text-muted-foreground/60">·</span>
          <span className="inline-flex items-center gap-1">
            {saving ? <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[color:var(--accent-amber)]" /> : <CheckCheck className="h-3 w-3 text-[color:var(--accent-green)]" />}
            {saving ? "Saving…" : "Saved"}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={onCopy} data-testid="content-card-copy-button" className="gap-1.5">
            <Copy className="h-3.5 w-3.5" /> Copy
          </Button>
          <Button size="sm" variant="outline" onClick={onSave} data-testid="content-card-save-button" className="gap-1.5">
            <Save className="h-3.5 w-3.5" /> Save
          </Button>
          <ScheduleAssetDialog
            asset={asset}
            trigger={(
              <Button size="sm" variant="outline" data-testid="content-card-schedule-button" className="gap-1.5">
                <CalendarPlus className="h-3.5 w-3.5" /> {asset.status === "scheduled" ? "Reschedule" : "Schedule"}
              </Button>
            )}
          />
          <Button size="sm" variant="ghost" data-testid="content-card-replace-button" className="gap-1.5 text-muted-foreground">
            <Replace className="h-3.5 w-3.5" /> Replace manually
          </Button>
        </div>
      </footer>
      <div className="border-t px-4 py-3" style={{ borderColor: "var(--border-subtle)" }}>
        <ApprovalActions
          onApprove={() => { approveAsset(asset.id); toast.success("Approved"); }}
          onRevise={() => setRevisionMode("revise")}
          onRegenerate={() => setRevisionMode("regenerate")}
          onReject={() => { rejectAsset(asset.id); toast.error("Rejected & archived"); }}
        />
        {revisionMode && (
          <div className="mt-3">
            <RevisionConsole
              dark
              asset={liveAsset}
              initialMode={revisionMode}
              onApply={onApplyRevision}
              onCancel={() => setRevisionMode(null)}
            />
          </div>
        )}
      </div>
    </article>
  );
}
